import { DraftIcon, FavoriteIcon, PlusIcon, UserIcon } from "@hightouch/htui";

import type {
  AmpFileSort,
  AmpFileType,
  AmpLibraryCountsQuery,
} from "src/graphql";
import { fileTypes } from "src/pages/projects/library/files/file-type";
import {
  DRAFTS_FOLDER_ID,
  FAVORITES_FOLDER_ID,
  NEW_FOLDER_ID,
  MY_FILES_FOLDER_ID,
} from "src/pages/projects/library/folder-paths";

export type View = "grid" | "table";

/** The Library URL's query param for the open folder or view. */
export const LIBRARY_FOLDER_PARAM = "folder";
/** The Library URL's query param that shows hidden files, which only staff in Dev mode can use. */
export const LIBRARY_HIDDEN_PARAM = "hidden";

export const SORT_LABELS: Record<AmpFileSort, string> = {
  newest: "Newest first",
  oldest: "Oldest first",
  updated: "Recently updated",
};

export const SORTS: readonly AmpFileSort[] = ["newest", "oldest", "updated"];

export const DEFAULT_LIBRARY_SORT: AmpFileSort = "newest";

export function isSort(value: string): value is AmpFileSort {
  return Object.hasOwn(SORT_LABELS, value);
}

/** The types the Library filters by, in menu order. */
export const LIBRARY_TYPES = [
  "canvas",
  "ad",
  "email",
  "sms",
  "push",
  "doc",
  "slides",
  "html",
  "upload",
] as const satisfies readonly AmpFileType[];

export type LibraryType = (typeof LIBRARY_TYPES)[number];

export const TYPE_LABELS: Record<LibraryType, string> = {
  canvas: "Canvases",
  ad: "Ads",
  email: "Emails",
  sms: "SMS",
  push: "Push",
  doc: "Docs",
  slides: "Slides",
  html: "Web pages",
  upload: "Uploads",
};

/** The views under Library, which stand in for a folder id. */
export const LIBRARY_VIEWS = [
  { id: MY_FILES_FOLDER_ID, name: "My files", icon: UserIcon },
  { id: FAVORITES_FOLDER_ID, name: "Favorites", icon: FavoriteIcon },
  ...LIBRARY_TYPES.map((type) => ({
    id: type,
    name: TYPE_LABELS[type],
    icon: fileTypes[type].icon,
  })),
];

const TYPE_IDS = new Set<string>(LIBRARY_TYPES);
/** The views in the Library sidebar: Favorites, then canvases. My files is a switch above the list instead. */
export const LIBRARY_TOP_VIEWS = LIBRARY_VIEWS.filter(
  ({ id }) =>
    id !== MY_FILES_FOLDER_ID && (!TYPE_IDS.has(id) || id === "canvas"),
);

/** The Library's New view, which heads its views and is where the product nav's Content opens. */
export const NEW_VIEW = {
  id: NEW_FOLDER_ID,
  name: "New",
  icon: PlusIcon,
};

/** Drafts, which stands in for a folder id and heads the Library because new work lands there. */
export const DRAFTS_VIEW = {
  id: DRAFTS_FOLDER_ID,
  name: "Drafts",
  icon: DraftIcon,
};

export type LibraryCounts = AmpLibraryCountsQuery["ampLibraryCounts"];

/** The file count for Drafts or a view from LIBRARY_VIEWS. */
export function libraryViewCount(counts: LibraryCounts, id: string) {
  if (id === DRAFTS_FOLDER_ID) return counts.drafts;
  if (id === MY_FILES_FOLDER_ID) return counts.mine;
  if (id === FAVORITES_FOLDER_ID) return counts.favorites;
  return counts.types.find(({ type }) => type === id)?.count ?? 0;
}

export function libraryFolderCount(counts: LibraryCounts, folderId: string) {
  return (
    counts.folders.find((folder) => folder.folderId === folderId)?.count ?? 0
  );
}
